import React, { useCallback, useEffect, useState } from 'react';
import { Button, Chip, Stack, Typography } from '@mui/material';
import {
	MobileDataList,
	MobileListCard,
	MobileListCardHeader,
} from '../../../Components/MobileDataList';
import { useRealtimeEvent } from '../../../Context/RealtimeContext';
import { makeApiRequest } from '../../../Shared/Api';
import { applyBidRealtimeToJobList } from '../../../Shared/bidRealtime';
import { BID_STATUS_COLORS, BID_STATUS_LABELS } from '../../../Shared/bids';
import {
	DEFAULT_JOBS_PAGE_SIZE,
	buildJobsListParams,
	fetchJobsList,
} from '../../../Shared/jobs';
import { formatCurrency } from '../../../Shared/stripeFee';
import JobsMobileFilters from './JobsMobileFilters';

const JobListCard = ({
	job,
	onJobClick,
	showJobStatus,
	statusLabels,
	statusColors,
}) => {
	const customerName = job.customer_name || job.customer;
	const bidStatus = job.bid_status;
	const dueLabel = job.due_date
		? new Date(job.due_date).toLocaleDateString()
		: null;

	return (
		<MobileListCard onClick={() => onJobClick(job.id)}>
			<Stack spacing={1.5}>
				<MobileListCardHeader
					title={job.name}
					subtitle={
						customerName ? (
							<>
								{customerName}
								{dueLabel ? (
									<Typography
										component="span"
										variant="caption"
										color="text.secondary"
										sx={{ display: 'block', mt: 0.25 }}
									>
										Due {dueLabel}
									</Typography>
								) : null}
							</>
						) : dueLabel ? (
							`Due ${dueLabel}`
						) : null
					}
					showChevron
				/>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={1}
					flexWrap="wrap"
					useFlexGap
				>
					<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
						{bidStatus ? (
							<Chip
								label={BID_STATUS_LABELS[bidStatus] || bidStatus}
								color={BID_STATUS_COLORS[bidStatus] || 'default'}
								size="small"
							/>
						) : null}
						{showJobStatus && job.job_status ? (
							<Chip
								label={statusLabels[job.job_status] || job.job_status}
								color={statusColors[job.job_status] || 'default'}
								size="small"
								variant="outlined"
							/>
						) : null}
					</Stack>
					<Typography
						variant="subtitle1"
						fontWeight={800}
						sx={{
							color: 'primary.main',
							fontVariantNumeric: 'tabular-nums',
							flexShrink: 0,
						}}
					>
						${formatCurrency(job.bid_amount)}
					</Typography>
				</Stack>
			</Stack>
		</MobileListCard>
	);
};

const JobsMobileList = ({
	onJobClick,
	showJobStatus,
	statusLabels,
	statusColors,
	jobStatusOptions = [],
	refreshKey = 0,
}) => {
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [pipeline, setPipeline] = useState('all');
	const [sort, setSort] = useState('created_desc');
	const [bidStatuses, setBidStatuses] = useState([]);
	const [jobStatuses, setJobStatuses] = useState([]);
	const [customers, setCustomers] = useState([]);
	const [customerOptions, setCustomerOptions] = useState([]);

	const [jobs, setJobs] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		let cancelled = false;
		makeApiRequest('GET', '/customers/')
			.then((response) => {
				if (cancelled) return;
				const options = (response || [])
					.map((customer) => ({
						value: String(customer.id),
						label: customer.name,
					}))
					.sort((a, b) => a.label.localeCompare(b.label));
				setCustomerOptions(options);
			})
			.catch(() => {
				if (!cancelled) setCustomerOptions([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const handleRealtimeUpdate = useCallback((message) => {
		setJobs((prev) => applyBidRealtimeToJobList(prev, message));
	}, []);

	useRealtimeEvent(handleRealtimeUpdate);

	const loadJobs = useCallback(
		async ({ page: nextPage, append }) => {
			if (append) {
				setIsLoadingMore(true);
			} else {
				setIsLoading(true);
			}

			try {
				const params = buildJobsListParams({
					page: nextPage,
					pageSize: DEFAULT_JOBS_PAGE_SIZE,
					search: debouncedSearch,
					pipeline,
					bidStatuses,
					jobStatuses,
					customers,
					sort,
				});
				const { results, count, next } = await fetchJobsList(params);
				setTotalCount(count);
				setHasMore(Boolean(next));
				setPage(nextPage);
				setJobs((prev) => (append ? [...prev, ...results] : results));
			} catch (error) {
				console.error('Error fetching jobs:', error);
				if (!append) {
					setJobs([]);
					setTotalCount(0);
					setHasMore(false);
				}
			} finally {
				setIsLoading(false);
				setIsLoadingMore(false);
			}
		},
		[debouncedSearch, pipeline, bidStatuses, jobStatuses, customers, sort],
	);

	useEffect(() => {
		loadJobs({ page: 1, append: false });
	}, [
		debouncedSearch,
		pipeline,
		sort,
		bidStatuses,
		jobStatuses,
		customers,
		refreshKey,
		loadJobs,
	]);

	const clearFilters = () => {
		setSearch('');
		setPipeline('all');
		setSort('created_desc');
		setBidStatuses([]);
		setJobStatuses([]);
		setCustomers([]);
	};

	const handleLoadMore = () => {
		if (isLoadingMore || !hasMore) return;
		loadJobs({ page: page + 1, append: true });
	};

	const hasActiveFilters =
		Boolean(search.trim()) ||
		pipeline !== 'all' ||
		sort !== 'created_desc' ||
		bidStatuses.length > 0 ||
		jobStatuses.length > 0 ||
		customers.length > 0;

	const emptyTitle =
		!hasActiveFilters && totalCount === 0 ? 'No jobs yet' : 'No matching jobs';
	const emptyDescription =
		!hasActiveFilters && totalCount === 0
			? 'Create your first job to start sending bids.'
			: 'Try resetting filters or adjusting your search.';

	return (
		<MobileDataList
			items={jobs}
			isLoading={isLoading}
			getKey={(job) => job.id}
			emptyTitle={emptyTitle}
			emptyDescription={emptyDescription}
			filters={
				<JobsMobileFilters
					search={search}
					onSearchChange={setSearch}
					pipeline={pipeline}
					onPipelineChange={setPipeline}
					sort={sort}
					onSortChange={setSort}
					bidStatuses={bidStatuses}
					onBidStatusesChange={setBidStatuses}
					jobStatuses={jobStatuses}
					onJobStatusesChange={setJobStatuses}
					customers={customers}
					onCustomersChange={setCustomers}
					customerOptions={customerOptions}
					jobStatusOptions={jobStatusOptions}
					showJobStatus={showJobStatus}
					resultCount={Math.min(jobs.length, totalCount)}
					totalCount={totalCount}
					onClear={clearFilters}
				/>
			}
			footer={
				hasMore ? (
					<Button
						variant="outlined"
						onClick={handleLoadMore}
						disabled={isLoadingMore}
						sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'center' }}
					>
						{isLoadingMore ? 'Loading...' : 'Load more'}
					</Button>
				) : null
			}
			renderItem={(job) => (
				<JobListCard
					job={job}
					onJobClick={onJobClick}
					showJobStatus={showJobStatus}
					statusLabels={statusLabels}
					statusColors={statusColors}
				/>
			)}
		/>
	);
};

export default JobsMobileList;
